import type { UsePrompt } from '@studio/design-system/prompt';
import { Message, usePrompt } from '@studio/design-system/prompt';
import { useLatestFn } from '@studio/react-utils/hooks';
import { createElement, useLayoutEffect } from 'react';
import type { ReactElement } from 'react';
import { newShowingId } from './incidents.js';
import { hideNotificationStack } from './stack-visibility.js';

/** `showSessionEnded()` options. */
export interface SessionEndedOptions {
  /** Runs when the person presses Sign in. */
  readonly onSignIn: () => void;
}

// Own the hold in the mounted prompt, not in its caller. Replacement and host
// unmount bypass onOpenChange; React cleanup covers those exits and StrictMode.
const SessionEndedMessage = (): ReactElement => {
  useLayoutEffect(hideNotificationStack, []);
  return createElement(Message, null, 'Sign in to pick up where you left off.');
};

/**
 * N8's pop-up (ENG-4930 item 8): "Your session ended" with one Sign in button
 * and no close X, Escape or outside-click dismissal, over the existing prompt.
 * Every corner notice is hidden while it is up and returns when it closes.
 * Wording: ENG-4862. Events (`alert_shown`, `alert_acted` sign_in) are the
 * caller's, per ENG-4874.
 *
 * @param prompt - Prompt controller from `usePrompt()`.
 * @param options - Sign-in handler.
 * @returns The showing ID for this display.
 */
export const showSessionEnded = (prompt: UsePrompt, { onSignIn }: SessionEndedOptions): string => {
  const showingId = newShowingId();

  prompt.show({
    title: 'Your session ended',
    message: createElement(SessionEndedMessage),
    okay: { label: 'Sign in', onClick: onSignIn },
    closeOnEscape: false,
    // Hidden rather than removed, as Local Mode's locked prompt does today.
    dismissClassName: 'hidden'
  });

  return showingId;
};

/**
 * Hook form of `showSessionEnded` for components.
 *
 * @returns `showSessionEnded` bound to this component's prompt.
 */
export const useShowSessionEnded = (): ((options: SessionEndedOptions) => string) => {
  const prompt = usePrompt();

  return useLatestFn((options: SessionEndedOptions) => showSessionEnded(prompt, options));
};
